import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { useWorkspaces } from '../../workspaces/useWorkspaces';
import { useFinishWelcome } from './useFinishWelcome';
import { WelcomeActions } from './WelcomeActions';
import { WelcomeLoop } from './WelcomeLoop';
import { SongsBrewTickBox } from './songsBrew/SongsBrewTickBox';
import { useSongsBrewTickBox } from './songsBrew/useSongsBrewTickBox';
import { welcomeMessages as messages } from './welcomeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole welcome page; each part uses its own slice of it */
import styles from './WelcomeView.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface MemberWelcomeProps {
  onExample: () => void;
  onOpenWorkspace: () => void;
  /** The first name of the owner who sent the invite. The API does not send it yet. */
  inviterName?: string | null;
}

/**
 * The invited team member (design: onboarding a7). No name field, no address and no
 * "Add my song": the owner runs the artists. Both buttons only mark the welcome as seen. The
 * SongsBrew tick box sits under the poster, above the buttons (design: songsbrew-promotion b3); the
 * subscription is for the member's own email.
 */
const MemberWelcomeComponent: React.FC<MemberWelcomeProps> = ({ onExample, onOpenWorkspace, inviterName = null }) => {
  const intl = useIntl();
  const { active } = useWorkspaces();
  const finishWelcome = useFinishWelcome();
  const [isBusy, setIsBusy] = useState(false);
  const songsBrew = useSongsBrewTickBox();
  const { subscribeSongsBrew } = songsBrew;

  const finishThen = useCallback(
    (next: () => void) => {
      setIsBusy(true);
      finishWelcome({ subscribeSongsBrew })
        .then(() => {
          setIsBusy(false);
          next();
        })
        .catch(console.error);
    },
    [finishWelcome, subscribeSongsBrew]
  );

  const workspaceName = active?.name ?? '';
  const title =
    workspaceName !== ''
      ? intl.formatMessage(messages.memberTitle, { workspace: workspaceName })
      : intl.formatMessage(messages.memberTitleNoName);
  // design: a7 note. Without the inviter, the line drops its first sentence.
  const lead =
    inviterName !== null && inviterName !== ''
      ? intl.formatMessage(messages.memberLead, { inviter: inviterName })
      : intl.formatMessage(messages.memberLeadNoInviter);

  return (
    <main className={styles.member}>
      <span className={styles.eyebrow}>
        <span className={styles.dot} />
        {intl.formatMessage(messages.memberEyebrow)}
      </span>
      <h1 className={clsx(styles.headline, styles.headlineSmall)}>{title}</h1>
      <p className={styles.lead}>{lead}</p>
      <WelcomeLoop wide />
      {songsBrew.isShown && (
        <SongsBrewTickBox checked={songsBrew.isTicked} onChange={songsBrew.setTicked} disabled={isBusy} />
      )}
      <WelcomeActions
        onExample={() => finishThen(onExample)}
        onOpenWorkspace={() => finishThen(onOpenWorkspace)}
        disabled={false}
        isBusy={isBusy}
      />
    </main>
  );
};

export const MemberWelcome = TypedMemo(MemberWelcomeComponent);
